/**
 * V13.14.5 / OPS V24 — Welcome Modal.
 *
 * Tier-aware first-login modal. Three variants:
 *   - free       → "Book a trial class" CTA → external pregnancy-yoga page
 *   - trial_paid → "We'll reach out within 24-36 hrs. Meanwhile, your EDD?"
 *   - elite      → "Welcome to Elite!" + EDD picker if missing
 *
 * Idempotency: backend tracks welcome_shown_at — once acknowledged, modal
 * never re-pops on subsequent logins.
 *
 * Sunday-aware copy: "starting Monday" if today is Saturday/Sunday.
 */
import { useState, useMemo } from 'react';
import api from '../lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Sparkles, Calendar, ExternalLink, Heart } from 'lucide-react';
import { toast } from 'sonner';

const PREGNANCY_YOGA_LANDING = 'https://www.soulsanskar.com/pregnancy-yoga';

export default function WelcomeModal({ state, onAcknowledged }) {
  const open = !!state?.welcome_pending;
  const kind = state?.welcome_kind || 'free';
  // V67 (founder 9 Oct 2026) — the backend tells us exactly what to do about the due date:
  //   ask     → nothing on file, let her set it once
  //   confirm → on file and locked (post-enrolment, usually sent by OPS with the payment)
  //             → show it read-only + "reach out to the team", never ask again
  //   edit    → on file and still editable
  const eddPrompt = state?.edd_prompt || (state?.edd ? (state?.edd_locked ? 'confirm' : 'edit') : 'ask');
  const needsEdd = eddPrompt === 'ask';
  const readOnlyEdd = eddPrompt === 'confirm';
  const [edd, setEdd] = useState(eddPrompt === 'edit' ? (state?.edd || '') : '');
  const [submitting, setSubmitting] = useState(false);

  const eddOnFile = state?.edd
    ? new Date(state.edd).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;
  const changeMessage = state?.edd_change_message
    || "This is the due date on your enrolment. If it needs to change, please reach out to our team and we'll update it for you.";

  // Sunday-aware copy: if today is Sat/Sun, classes start "from Monday".
  const startsMondayCopy = useMemo(() => {
    const day = new Date().getDay();
    return day === 0 || day === 6 ? ' from Monday' : '';
  }, []);

  const closeWith = async (extra = {}) => {
    setSubmitting(true);
    try {
      const res = await api.post('/api/learner/welcome/acknowledge', extra);
      onAcknowledged?.(res.data?.state);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = typeof detail === 'object' ? detail?.code : null;
      // She must NEVER be trapped in a modal she cannot dismiss: if the due date is
      // refused, show why and close the modal anyway.
      if (extra.edd) {
        toast.error((typeof detail === 'object' ? detail?.message : detail) || changeMessage);
        const { edd: _dropped, ...rest } = extra;
        try {
          const res2 = await api.post('/api/learner/welcome/acknowledge', rest);
          onAcknowledged?.(res2.data?.state);
        } catch (_) {
          onAcknowledged?.(null);
        }
      } else {
        toast.error((typeof detail === 'object' ? detail?.message : detail) || 'Could not save');
        if (code) onAcknowledged?.(null);
      }
    }
    setSubmitting(false);
  };

  const acknowledgeAndOpenLanding = async () => {
    await closeWith({ welcome_kind: kind, cta_clicked: 'book_trial' });
    window.open(PREGNANCY_YOGA_LANDING, '_blank', 'noopener,noreferrer');
  };

  const acknowledgeWithEdd = async () => {
    if (needsEdd && !edd) {
      toast.error('Please pick your EDD to continue');
      return;
    }
    const payload = { welcome_kind: kind, cta_clicked: 'continue' };
    // Never re-post a locked date — the backend accepts it now, but there is no reason to.
    if (edd && !readOnlyEdd) payload.edd = edd;
    await closeWith(payload);
  };

  const justClose = () => closeWith({ welcome_kind: kind, cta_clicked: 'dismiss' });

  if (!open) return null;

  // ─── Variant: free (no payment yet) ───
  if (kind === 'free') {
    return (
      <Dialog open onOpenChange={(v) => !v && justClose()}>
        <DialogContent className="sm:max-w-md text-center overflow-hidden"
                       data-testid="welcome-modal-free">
          <div className="absolute inset-0 pointer-events-none opacity-20"
               style={{
                 background:
                   'radial-gradient(circle at 30% 20%, #fbbf24 0%, transparent 60%), '
                   + 'radial-gradient(circle at 80% 80%, #f43f5e 0%, transparent 50%)',
               }} />
          <div className="relative space-y-3 py-2">
            <div className="h-16 w-16 mx-auto rounded-full bg-gradient-to-br from-rose-300 to-pink-500 flex items-center justify-center shadow-md">
              <Sparkles className="h-8 w-8 text-white" />
            </div>
            <DialogHeader className="text-center">
              <DialogTitle className="font-heading text-2xl">
                Welcome to Soul Sanskar 💕
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground px-4">
              You're all set up. Book a free trial class to experience the
              guided practice we've built for your stage.
            </p>
            <DialogFooter className="flex-col sm:flex-col gap-2 pt-3">
              <Button
                onClick={acknowledgeAndOpenLanding}
                className="w-full bg-rose-600 hover:bg-rose-700"
                disabled={submitting}
                data-testid="welcome-book-trial-btn"
              >
                <ExternalLink className="h-4 w-4 mr-1" />
                Book trial now
              </Button>
              <Button variant="ghost" onClick={justClose}
                      data-testid="welcome-skip-btn" disabled={submitting}>
                I'll explore first
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // ─── Variant: trial_paid (paid yoga trial — awaiting Elite onboarding) ───
  if (kind === 'trial_paid') {
    return (
      <Dialog open onOpenChange={() => { /* must complete or skip explicitly */ }}>
        <DialogContent className="sm:max-w-md overflow-hidden"
                       data-testid="welcome-modal-trial-paid"
                       onPointerDownOutside={(e) => e.preventDefault()}>
          <div className="space-y-4">
            <div className="h-14 w-14 mx-auto rounded-full bg-gradient-to-br from-amber-300 to-rose-400 flex items-center justify-center shadow-md">
              <Heart className="h-7 w-7 text-white" />
            </div>
            <DialogHeader className="text-center">
              <DialogTitle className="font-heading text-2xl">
                Welcome — payment received 💕
              </DialogTitle>
            </DialogHeader>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-900">
              Our team will reach out within 24–36 hours to set up your
              schedule and answer any questions{startsMondayCopy}.
            </div>
            {needsEdd ? (
              <div>
                <p className="text-sm font-medium mb-1">
                  Meanwhile, when's your expected due date?
                </p>
                <p className="text-[11px] text-muted-foreground mb-2">
                  We use this to personalise your yoga sequences week by week.
                </p>
                <Label htmlFor="welcome-edd" className="sr-only">EDD</Label>
                <Input
                  id="welcome-edd" type="date" value={edd}
                  onChange={(e) => setEdd(e.target.value)}
                  data-testid="welcome-edd-input"
                />
              </div>
            ) : readOnlyEdd ? (
              <div className="rounded-xl border bg-muted/40 p-3" data-testid="welcome-edd-readonly">
                <p className="text-xs text-muted-foreground">Your due date</p>
                <p className="text-sm font-medium" data-testid="welcome-edd-value">{eddOnFile}</p>
                <p className="text-[11px] text-muted-foreground mt-1">{changeMessage}</p>
              </div>
            ) : (
              <div>
                <p className="text-sm font-medium mb-1">Your expected due date</p>
                <Input
                  id="welcome-edd" type="date" value={edd}
                  onChange={(e) => setEdd(e.target.value)}
                  data-testid="welcome-edd-input"
                />
              </div>
            )}
            <DialogFooter className="flex-col sm:flex-col gap-2">
              <Button
                onClick={acknowledgeWithEdd}
                disabled={submitting || (needsEdd && !edd)}
                className="w-full bg-rose-600 hover:bg-rose-700"
                data-testid="welcome-trial-continue-btn"
              >
                <Calendar className="h-4 w-4 mr-1" />
                {needsEdd ? 'Save & continue' : 'Continue to dashboard'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // ─── Variant: elite (enrolment confirmed — full Elite member) ───
  return (
    <Dialog open onOpenChange={() => { /* must complete or skip explicitly */ }}>
      <DialogContent className="sm:max-w-md overflow-hidden"
                     data-testid="welcome-modal-elite"
                     onPointerDownOutside={(e) => e.preventDefault()}>
        <div className="space-y-4">
          <div className="h-16 w-16 mx-auto rounded-full bg-gradient-to-br from-amber-400 to-rose-500 flex items-center justify-center shadow-lg">
            <Sparkles className="h-8 w-8 text-white" />
          </div>
          <DialogHeader className="text-center">
            <DialogTitle className="font-heading text-2xl">
              Welcome to Elite ✨
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground text-center px-4">
            Your enrolment is complete. You now have access to unlimited live
            classes, on-demand courses and doctor consults.
            We're delighted to have you{startsMondayCopy}.
          </p>
          {needsEdd ? (
            <div className="bg-rose-50 border border-rose-100 rounded-xl p-3">
              <p className="text-sm font-medium mb-1">One last detail —</p>
              <p className="text-[11px] text-muted-foreground mb-2">
                When's your expected due date? Your access window is bound to
                this, so it'll be locked once you save it.
              </p>
              <Label htmlFor="welcome-edd-elite" className="sr-only">EDD</Label>
              <Input
                id="welcome-edd-elite" type="date" value={edd}
                onChange={(e) => setEdd(e.target.value)}
                data-testid="welcome-edd-input-elite"
              />
            </div>
          ) : readOnlyEdd ? (
            <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3" data-testid="welcome-edd-readonly-elite">
              <p className="text-xs text-emerald-900/70">Your due date</p>
              <p className="text-sm font-medium text-emerald-950" data-testid="welcome-edd-value-elite">{eddOnFile}</p>
              <p className="text-[11px] text-emerald-900/80 mt-1">{changeMessage}</p>
            </div>
          ) : (
            <div className="bg-rose-50 border border-rose-100 rounded-xl p-3">
              <p className="text-sm font-medium mb-1">Your expected due date</p>
              <Label htmlFor="welcome-edd-elite" className="sr-only">EDD</Label>
              <Input
                id="welcome-edd-elite" type="date" value={edd}
                onChange={(e) => setEdd(e.target.value)}
                data-testid="welcome-edd-input-elite"
              />
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-col gap-2">
            <Button
              onClick={acknowledgeWithEdd}
              disabled={submitting || (needsEdd && !edd)}
              className="w-full bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-600 hover:to-rose-600"
              data-testid="welcome-elite-continue-btn"
            >
              {readOnlyEdd ? 'Save & continue' : needsEdd ? 'Save EDD & enter' : 'Save & continue'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
